/**
 * What the success page can know, and how.
 *
 * **Nothing in the URL says which order.** `OnlinePaymentService.Landing` is an enum of two bare paths —
 * `SUCCESS("/success")` and `ERROR("/payment-error")` — and the controller redirects to
 * `customerBaseUrl + landing.path()` with no query string. The page is order-blind by construction.
 *
 * So the checkout leaves a note in `sessionStorage` before handing the browser to the bank, and this
 * reads it. **Each key is removed on read**, so a refresh cannot re-claim anything.
 *
 * It survives the trip to the bank and back because that is the same tab and the same origin. It does
 * **not** survive the customer finishing in a different tab, or an in-app browser handing off to the
 * system one. When it is gone the page says nothing rather than guessing — which is the right failure,
 * and is stated here rather than left to look like an oversight.
 */

const KEYS = {
  /** The points this order earned, as the checkout computed them. */
  points: 'fk_order_points',
  orderId: 'fk_scratch_order_id',
  orderNumber: 'fk_order_number',
} as const

function takeOnce(key: string): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    const value = sessionStorage.getItem(key)
    if (value !== null) sessionStorage.removeItem(key)
    return value
  } catch {
    // A locked-down profile throws rather than returning null, and losing a banner is better than
    // taking the landing page down.
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // Same reason. The page degrades to saying nothing.
  }
}

export interface OrderHandoff {
  points: number | null
  orderId: number | null
  orderNumber: string | null
}

/** Left by the checkout immediately before the browser is handed to the bank. */
export function rememberOrder(handoff: OrderHandoff): void {
  write(KEYS.points, handoff.points === null ? null : String(handoff.points))
  write(KEYS.orderId, handoff.orderId === null ? null : String(handoff.orderId))
  write(KEYS.orderNumber, handoff.orderNumber)
}

/**
 * Read once, then gone.
 *
 * A value that will not parse is `null`, not zero — *no points banner* and *you earned nothing* are
 * different statements, and only one of them is safe to make without the data.
 */
export function takeOrderHandoff(): OrderHandoff {
  const points = takeOnce(KEYS.points)
  const orderId = takeOnce(KEYS.orderId)
  const orderNumber = takeOnce(KEYS.orderNumber)
  const asNumber = (value: string | null): number | null => {
    if (value === null || value.trim() === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return {
    points: asNumber(points),
    orderId: asNumber(orderId),
    orderNumber: orderNumber === null || orderNumber.trim() === '' ? null : orderNumber,
  }
}

/** Whether there is a points banner to draw. Zero earned is not worth a banner either. */
export function hasPointsToShow(handoff: OrderHandoff): boolean {
  return handoff.points !== null && handoff.points > 0
}
